// Universal summary events handler - reusable for file-list and fulltext search

import { showSummaryPopup, hideSummaryPopup, updateSummaryContent, getSummaryState, setSummaryState } from './summary-popup.module.js';

/**
 * Initialize universal summary events (hover + click)
 * @param {Object} options - Configuration options
 * @param {jQuery|string} options.container - Container selector or jQuery object
 * @param {string} options.chipSelector - Selector for summary chip/icon (e.g., '.fulltext-summary__chip', '.summary-icon')
 * @param {Function} options.getFileData - Function(element) => {file, fileKey, nameKey} - Extract file data from DOM element
 * @param {boolean} options.mobileClickOnly - If true, click only works on mobile (default: true)
 */
export function initSummaryEventsUniversal(options) {
	const { container, chipSelector, getFileData, mobileClickOnly = true } = options;
	const $container = typeof container === 'string' ? $(container) : container;
	
	if (!$container.length) {
		console.warn('[SummaryEvents] Container not found:', container);
		return;
	}
	
	// Helper function to show summary popup
	async function handleSummaryClick(e, target) {
		// Only handle on mobile devices if mobileClickOnly is true
		if (mobileClickOnly && window.innerWidth >= 1200) return;
		
		e.preventDefault();
		e.stopPropagation();
		
		const $chip = $(target).is(chipSelector) ? $(target) : $(target).closest(chipSelector);
		if (!$chip.length) return;
		
		const fileData = getFileData($chip[0]);
		if (!fileData || !fileData.file || !fileData.fileKey) {
			console.warn('[SummaryEvents] Could not extract file data from element');
			return;
		}
		
		const $icon = $chip.find('i.bi-anthropic').length ? $chip.find('i.bi-anthropic') : $chip;
		await showSummaryPopup(fileData.file, fileData.fileKey, $icon);
	}
	
	// Click handler for mobile devices
	$container.off('click.summaryUniversal', chipSelector)
		.on('click.summaryUniversal', chipSelector, async function(e) {
			await handleSummaryClick(e, this);
		});
	
	// Touch handler for mobile devices
	$container.off('touchend.summaryUniversal', chipSelector)
		.on('touchend.summaryUniversal', chipSelector, async function(e) {
			if (mobileClickOnly && window.innerWidth >= 1200) return;
			e.preventDefault();
			e.stopPropagation();
			await handleSummaryClick(e, this);
		});
	
	// Hover handlers for desktop (only on desktop)
	$container.off('mouseenter.summaryUniversal mouseleave.summaryUniversal', chipSelector)
		.on('mouseenter.summaryUniversal', chipSelector, async function(e) {
			// Only handle hover on desktop
			if (window.innerWidth < 1200) return;
			
			const $chip = $(this);
			const fileData = getFileData($chip[0]);
			if (!fileData || !fileData.file || !fileData.fileKey) {
				return;
			}
			
			const $icon = $chip.find('i.bi-anthropic').length ? $chip.find('i.bi-anthropic') : $chip;
			const file = fileData.file;
			const fileKey = fileData.fileKey;
			
			let state = getSummaryState();
			state.summaryIconHovered = true;
			
			if (state.summaryHoverTimeout) {
				clearTimeout(state.summaryHoverTimeout);
				state.summaryHoverTimeout = null;
			}
			
			state.currentSummaryFile = { file, fileKey, $icon };
			setSummaryState(state);
			
			const timeoutId = setTimeout(async () => {
				const currentState = getSummaryState();
				if (currentState.currentSummaryFile && currentState.currentSummaryFile.fileKey === fileKey) {
					const { openDatabase, closeDatabase, getSummariesByKeys } = await import('../../storage/idb-files.js');
					
					try {
						const db = await openDatabase();
						
						// Try multiple key variations
						const fileName = file.n || file.name || '';
						const filePath = file.p || file.path || '';
						const nameKey = fileName || fileKey.split('/').pop() || '';
						
						let summaries = await getSummariesByKeys(db, [fileKey]);
						if (!summaries || !summaries[0]) {
							summaries = await getSummariesByKeys(db, [nameKey]);
						}
						if ((!summaries || !summaries[0]) && filePath && filePath !== fileKey) {
							summaries = await getSummariesByKeys(db, [filePath]);
						}
						
						closeDatabase(db);
						
						const summary = summaries && summaries[0];
						if (summary) {
							if (currentState.summaryPopupElement && currentState.summaryPopupElement.length) {
								// Update existing popup content
								updateSummaryContent(file, fileKey, summary);
							} else {
								// Show new popup
								showSummaryPopup(file, fileKey, $icon);
							}
						}
					} catch (error) {
						console.error('[SummaryEvents] Error loading summary:', error);
					}
				}
			}, 500); // 500ms delay before showing popup
			
			state.summaryHoverTimeout = timeoutId;
			setSummaryState(state);
		})
		.on('mouseleave.summaryUniversal', chipSelector, async function(e) {
			// Only handle hover on desktop
			if (window.innerWidth < 1200) return;
			
			let state = getSummaryState();
			state.summaryIconHovered = false;
			setSummaryState(state);
			
			if (state.summaryHoverTimeout) {
				clearTimeout(state.summaryHoverTimeout);
				state.summaryHoverTimeout = null;
				setSummaryState(state);
			}
			
			const hideTimeout = setTimeout(async () => {
				const currentState = getSummaryState();
				if (currentState.summaryPopupElement && currentState.summaryPopupElement.length) {
					if (!currentState.summaryPopupHovered && !currentState.summaryIconHovered) {
						currentState.currentSummaryFile = null;
						setSummaryState(currentState);
						hideSummaryPopup();
					}
				} else {
					state.currentSummaryFile = null;
					setSummaryState(state);
				}
			}, state.SUMMARY_POPUP_HIDE_DELAY);
			
			state.summaryPopupTimeout = hideTimeout;
			setSummaryState(state);
		});
}

